import { useId, useRef, useState } from 'react'
import { IconoAviso, IconoHoja } from '../../componentes/iconos'
import { EXTENSIONES, ExcelIlegible, leerExcel } from './excel'

/**
 * Arrastrar la hoja del reparto en vez de copiar y pegar las celdas.
 *
 * Lo que sale del fichero **se vuelca en el cuadro de texto de abajo**, a la
 * vista y editable, en lugar de saltar directo a la revision. Es deliberado: un
 * fichero que se traga entero y devuelve una tabla ya montada esconde lo que se
 * ha leido, y la primera vez que una columna venga corrida nadie sabria por que.
 *
 * La zona de arrastre no es el unico camino: el mismo bloque es un `label` de un
 * `input type=file`, asi que con teclado se abre el selector de siempre.
 */

interface Props {
  onTexto: (texto: string) => void
}

const ACEPTA = EXTENSIONES.join(',')

export function SubirExcel({ onTexto }: Props) {
  const idCampo = useId()
  const entrada = useRef<HTMLInputElement>(null)
  const [encima, setEncima] = useState(false)
  const [leyendo, setLeyendo] = useState(false)
  const [error, setError] = useState('')
  const [nombre, setNombre] = useState('')

  const procesar = async (fichero: File | undefined) => {
    if (!fichero) return
    setError('')
    setLeyendo(true)
    try {
      const { texto, pestana } = await leerExcel(fichero)
      setNombre(pestana === '' ? fichero.name : `${fichero.name} (pestana "${pestana}")`)
      onTexto(texto)
    } catch (fallo) {
      setNombre('')
      setError(
        fallo instanceof ExcelIlegible
          ? fallo.message
          : 'No se ha podido leer el fichero. Prueba a pegar las celdas en el cuadro de abajo.',
      )
    } finally {
      setLeyendo(false)
      // Sin esto, volver a soltar el MISMO fichero no dispara `change`.
      if (entrada.current) entrada.current.value = ''
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={idCampo}
        onDragOver={(evento) => {
          evento.preventDefault()
          setEncima(true)
        }}
        onDragLeave={() => setEncima(false)}
        onDrop={(evento) => {
          evento.preventDefault()
          setEncima(false)
          void procesar(evento.dataTransfer.files[0])
        }}
        className={`flex cursor-pointer flex-col items-center gap-1 rounded-lg border border-dashed px-4 py-6 text-center transition-colors ${
          encima ? 'border-foco bg-acento-suave' : 'border-borde-fuerte bg-elevado/40 hover:bg-elevado'
        }`}
      >
        <IconoHoja aria-hidden="true" className="size-6 text-tinta-apagada" />
        <span className="text-sm font-medium text-tinta-2">
          {leyendo ? 'Leyendo la hoja' : 'Arrastra aqui el Excel del reparto'}
        </span>
        <span className="text-sm text-tinta-apagada">
          O pulsa para buscarlo. Se lee la primera pestana; los datos no salen de tu navegador.
        </span>
        <input
          ref={entrada}
          id={idCampo}
          type="file"
          accept={ACEPTA}
          className="sr-only"
          onChange={(evento) => void procesar(evento.target.files?.[0])}
        />
      </label>

      {nombre !== '' ? (
        <p className="text-sm text-tinta-apagada">
          Leido <span className="font-medium text-tinta-2">{nombre}</span>. Revisa el cuadro de
          abajo antes de resolver.
        </p>
      ) : null}

      {error !== '' ? (
        <p role="alert" className="flex items-start gap-2 text-sm text-aviso-tinta">
          <IconoAviso aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-aviso-icono" />
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  )
}
